/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { Link, useLocation } from 'react-router-dom'
import { buildEventLinkState } from '../utils/eventReturnTo'
import { ROUTES } from '@sportninja/common/constants/app'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import Text from 'src/components/ts-components/Text'
import MapMarkerIcon from 'src/components/icons/MapMarkerIcon'
import Icon from 'src/components/Icon'
import { font, weightSemiBold } from 'src/components/css'
import type { SnEvent } from '@sportninja/common/features/event/types'
import { formatEventLocationFull } from '@sportninja/common/features/event/formatEventLocation'
import { getEventCategory } from '@sportninja/common/features/event/eventCategories'
import { resolveEventCompetition } from '@sportninja/common/features/event/eventOwnership'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'
import { useEventRowTimes } from '../hooks/useEventRowTimes'

/**
 * Derived from `SnEvent` rather than redeclared, so a change to the API shape
 * surfaces as a compile error here instead of a silently blank row.
 */
export type EventListRowItem = Pick<
  SnEvent,
  | 'id'
  | 'name'
  | 'starts_at'
  | 'timezone'
  | 'venue'
  | 'facility'
  | 'address'
  // Read by `formatEventLocationFull`. Optional on SnEvent, so omitting it
  // compiles — but then this type stops guaranteeing what its doc claims.
  | 'address_name'
  | 'cancelled'
  | 'category'
  | 'team'
  // All three feed `resolveEventCompetition`, which names the competition
  // under the category badge: `event_type` gates it to competition events,
  // and `root_schedule` is preferred over `schedule` (which may be a
  // division) for the name itself.
  | 'event_type'
  | 'schedule'
  | 'root_schedule'
  // The description, shown in the right column. Called `details` on the wire.
  | 'details'
>

interface EventListRowProps {
  event: EventListRowItem
  hasNextSibling: boolean
}

/**
 * Distinct row for an event inside the schedule list.
 *
 * Deliberately mirrors `GameListRowV2`'s skeleton so a mixed game+event list
 * scans as one column rather than making the eye jump. A header strip carries
 * a centred "Event: <name>", occupying the same band the game row gives its
 * role badges; below it the date and location are centred, aligning with the
 * game row's own centred date/venue block.
 *
 * Single responsive component (no separate mobile variant): the content is a
 * centred text stack at every width, unlike the game row's home/score/away
 * layout which has to restructure on small screens.
 */
/* Same asset the detail page falls back to, so a team with no crest looks
   identical in both places. */
const TEAM_PLACEHOLDER = '/images/generic_placeholders/placeholder_team.png'

const EventListRow = ({ event, hasNextSibling }: EventListRowProps) => {
  const { viewerTime, eventTime } = useEventRowTimes({
    startsAt: event.starts_at,
    timezone: event.timezone,
  })
  // Same venue/facility/address triple games carry, formatted identically.
  // Name AND street, not just one: the row has the width for both, and a
  // reader scanning the schedule wants the place's name to recognise it plus
  // the street to find it.
  const location = formatEventLocationFull(event)
  // Router location, distinct from `location` above (the event's VENUE) — the
  // list URL this row is being rendered on.
  const routerLocation = useLocation()
  const isCancelled = Boolean(event.cancelled)
  // Null when the event has no category; an unrecognised one still resolves,
  // with the neutral fallback colour.
  const category = getEventCategory(event.category)
  // `name_full` first, matching the detail page — the list response sends both
  // and the fuller form is the one the game rows show.
  const teamName = event.team?.name_full || event.team?.name
  // Never the raw `full_path`: resolveAssetUrl rewrites the S3 host to the
  // active CDN. Undefined for a team with no crest, which falls to the
  // placeholder alongside a broken-URL onError.
  const teamLogo = resolveAssetUrl(event.team?.image)
  // The whole left slot is conditional on the NAME, not the logo: a crest with
  // no label identifies nothing, whereas a name alone still does.
  const hasTeam = Boolean(teamName)
  // Trimmed so a whitespace-only description does not open the column's
  // clamp for nothing.
  const description = event.details?.trim() || ''
  // Null for a team event, and for a competition event whose payload carries
  // no usable id/name pair — the badge then stands alone.
  const competition = resolveEventCompetition(event)

  return (
    <Link
      // Carries the list the user is reading, so a delete on the event page
      // returns HERE rather than to the event's owner — the two differ for a
      // competition event opened from a team's schedule. Read off the current
      // location, so this shared row records whichever list it renders in.
      to={{
        pathname: `${ROUTES.EVENT_ROOT}/${event.id}`,
        state: buildEventLinkState(routerLocation),
      }}
      css={css`
        text-decoration: none;
        display: block;
        ${hasNextSibling && 'margin-bottom: 16px;'}
      `}
    >
      <div css={[containerStyles, isCancelled && containerCancelledStyles]}>
        {/* Oversized calendar watermark bleeding off the left edge — a quiet
            visual cue that separates an event card from a game card at a
            glance. Decorative only, so it is hidden from assistive tech and
            ignores pointer events. */}
        <span css={watermarkStyles} aria-hidden='true'>
          <Icon name='calendar-alt' fontSize={120} color={colors.NEUTRAL_0} />
        </span>

        {/* Occupies the same strip the game row uses for its role badges, so
            the two row types line up vertically instead of staggering.

            The generic EVENT badge is gone — the "Event:" prefix before the
            title carries the type. Both side slots are now empty spacers: the
            CATEGORY badge moved down into the centre column, above the date. */}
        <div css={headerStyles}>
          {/* A bare spacer balancing the right-hand badge, so the title stays
              optically centred. `aria-hidden` because it holds nothing to
              read. */}
          <span css={headerSideStyles} aria-hidden='true' />
          <span css={titleGroupStyles}>
            {/* Accent-coloured so the label reads as a type marker rather than
                part of the event's own name. Greyed but NOT struck through on
                a cancelled row — the strike belongs to the name; striking the
                type would read as "Event" itself being cancelled.

                Deliberately a bare "Event:" and not the owner's name — the
                centre column carries the category badge and, for a competition
                event, the competition it belongs to. */}
            <span
              css={[typePrefixStyles, isCancelled && typePrefixMutedStyles]}
            >
              Event:
            </span>
            <span css={[titleStyles, isCancelled && titleCancelledStyles]}>
              {capitalizeWords(event.name ?? '')}
            </span>
          </span>
          {/* Empty, balancing the left spacer so the title stays optically
              centred. The category badge that used to sit here moved into the
              centre column, above the date. */}
          <span
            css={[headerSideStyles, headerSideEndStyles]}
            aria-hidden='true'
          />
        </div>

        {/* Date and location centred, matching the game row's middle column so
            the eye tracks one line down a mixed list. Two times when the
            event's zone differs from the viewer's, exactly as a game row does. */}
        <div css={bodyStyles}>
          {/* Team identity sits in the BODY's left column, not the header —
              mirroring the game card, which also carries crest + name beside
              its centred date/venue block rather than in its badge strip.

              ALWAYS rendered, empty when the event has no team: it is the
              counterweight to the description column opposite, and the centre
              stack only sits true when both sides hold their width. */}
          <span css={teamStyles} aria-hidden={hasTeam ? undefined : 'true'}>
            {hasTeam && (
              <>
                <img
                  src={teamLogo ?? TEAM_PLACEHOLDER}
                  alt=''
                  css={teamLogoStyles}
                  onError={(e) => {
                    e.currentTarget.src = TEAM_PLACEHOLDER
                  }}
                />
                {/* Rendered through `Text` rather than a styled span so it picks
                  up the SAME font the game card's team names get: `Text`
                  overrides heading5's `font.title` with `font.heading` (the
                  mixed-case condensed face — BarlowCondensed on SportNinja).
                  Restating the preset in emotion would stop at `font.title`,
                  which is Rift, and on Canlan would force uppercase too. */}
                <Text
                  variant='heading5'
                  weight='semiBold'
                  color={isCancelled ? colors.NEUTRAL_50 : colors.NEUTRAL_0}
                  style={teamNameStyle}
                >
                  {teamName}
                </Text>
              </>
            )}
          </span>

          <div css={bodyCenterStyles}>
            {/* Category badge above the date, not opposite the title: it
                labels the event rather than flagging a status, so it reads as
                an introduction to the date block. CANCELLED still outranks it
                — a called-off event needs that state read first. A competition
                event names its competition directly beneath, so the two lines
                answer "what kind, and which competition" together. */}
            {(isCancelled || category || competition) && (
              <span css={categoryGroupStyles}>
                {/* ABOVE the badge: the competition is the context the event
                    sits in, so it reads as the block's dateline — where it
                    sits, then what kind it is, then when.

                    Same treatment the game row gives its own season line (see
                    CenterInfo's `scheduleName`): captionXSmall / semiBold /
                    NEUTRAL_50 / uppercase, so a mixed list of games and events
                    labels its competition identically on both row types. */}
                {competition && (
                  <Text
                    variant='captionXSmall'
                    weight='semiBold'
                    color={colors.NEUTRAL_50}
                    style='text-transform: uppercase; text-align: center;'
                  >
                    {competition.name}
                  </Text>
                )}
                {isCancelled ? (
                  <span css={cancelledTagStyles}>Cancelled</span>
                ) : category ? (
                  <span
                    css={categoryTagStyles}
                    style={{
                      backgroundColor: category.color,
                      color: category.textColor,
                    }}
                  >
                    {category.label}
                  </span>
                ) : null}
              </span>
            )}
            {viewerTime && (
              <Text
                variant='paragraphSmall'
                weight='medium'
                color={colors.NEUTRAL_0}
              >
                {viewerTime}
              </Text>
            )}
            {eventTime && (
              <Text
                variant='paragraphXSmall'
                weight='regular'
                color={colors.NEUTRAL_50}
                style='text-align: center;'
              >
                {eventTime}
              </Text>
            )}
            {location && (
              <div css={locationRowStyles}>
                <MapMarkerIcon size={14} color={colors.NEUTRAL_50} />
                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                >
                  {location}
                </Text>
              </div>
            )}
          </div>

          {/* ALWAYS rendered, whether or not there is a team or a description:
              it is the counterweight to the team column, and the centre stack
              is only truly centred when both sides are present. Gating it on
              either one is what made a row with no team sit off-centre against
              its neighbours.

              When it does have text, that text is the description — same
              treatment as the address line, so the two secondary details read
              as one register. */}
          <span
            css={bodyDescriptionStyles}
            aria-hidden={description ? undefined : 'true'}
          >
            {description && (
              <Text
                variant='paragraphXSmall'
                weight='regular'
                color={colors.NEUTRAL_50}
                // Passed through Text's own `style` rather than set from the
                // parent: Text appends `style` last inside its css block, so
                // a rule declared out here would lose a specificity tie.
                style='text-align: center;'
              >
                {description}
              </Text>
            )}
          </span>
        </div>
      </div>
    </Link>
  )
}

export default EventListRow

// ---- styled ----

/* Differentiating an event from a game uses THREE redundant signals rather than
   background value alone:

     1. a 4px PRIMARY left rail   — the dominant cue; reads instantly as "other
                                    kind of thing" without asking the eye to
                                    compare two dark greys
     2. a PRIMARY-tinted header   — see headerStyles
     3. an "Event:" prefix before the title — see typePrefixStyles
     4. a lighter card body       — supporting, not carrying, the distinction

   Value alone (the previous SECONDARY_400 → SECONDARY_300 step) is the weakest
   possible cue: it collapses on dim/bright displays and for low-vision users.
   The rail also survives greyscale, where a hue-only signal would not.

   The accent is `EVENT_ACCENT` below — one token the whole row derives from,
   so the event identity can be retuned in a single place. */

/*
 * The accent every part of this row is built from: the rail, the border, both
 * tints, and the "Event:" marker.
 *
 * Currently `INFO_300` — the info blue — being trialled in place of `PRIMARY`.
 *
 * Note this reads differently per tenant: on SportNinja it is a real change
 * (amber -> blue), while on Canlan PRIMARY is ALREADY a blue (#147efb), so
 * this is only a shift in shade there. Swap this one line back to
 * `colors.PRIMARY` to restore the tenant-switched accent.
 */
const EVENT_ACCENT = colors.INFO_300

const containerStyles = css`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  /* Same tint technique as the header, two steps lighter in alpha (5% vs 15%)
     so the body sits BELOW the header in the value stack while staying in the
     PRIMARY family. Composited: body #262929, header #3b3825, game card
     #1b222b — the event card reads warm and tinted, and still lifts off the
     darker game row above it. */
  background-color: ${colors.SECONDARY_400};
  background-image: linear-gradient(${EVENT_ACCENT}0D, ${EVENT_ACCENT}0D);
  /* PRIMARY at 40% rather than grey SOFT_STEEL — a grey outline fights the
     amber tint inside it. Kept at partial alpha, not solid: the border traces
     the whole card, so a full-strength accent would out-shout the 4px rail
     that is meant to be the dominant cue. */
  border: 1px solid ${EVENT_ACCENT}66;
  /* The rail replaces the left border rather than sitting inside it, so the
     card's content box stays aligned with the game row above it. */
  border-left: 4px solid ${EVENT_ACCENT};
  border-radius: 8px;
  /* Clips the watermark where it bleeds past the rounded corners. */
  overflow: hidden;
  transition: filter 0.2s ease-in-out;

  &:hover {
    filter: brightness(1.08);
  }
`

/* A cancelled event drops BOTH accent borders to neutral — it is still an
   event, but it should not claim the same attention as one that is going ahead.
   Greying only the rail would leave an accent outline around a struck-through
   card, which reads as a mistake. The CANCELLED pill carries the state. */
const containerCancelledStyles = css`
  border-color: ${colors.SOFT_STEEL};
  border-left-color: ${colors.NEUTRAL_50};
`

/* Tilted calendar watermark, inset from the RIGHT edge and spanning the full
   card height so it passes behind the header rather than starting below it.
   Very faint — texture, not an icon.

   Wrapping the Icon rather than styling it directly keeps the opacity and
   transform off Icon's own emotion class, which sets its color.

   0.04 rather than 0.025: at the lower value it read as a rendering artifact
   rather than deliberate texture. It stays well below the content so it never
   competes with the title. */
const watermarkStyles = css`
  position: absolute;
  right: 40px;
  top: 50%;
  transform: translateY(-50%) rotate(-18deg);
  transform-origin: center;
  z-index: 1;
  line-height: 0;
  opacity: 0.04;
  pointer-events: none;
  user-select: none;
`

/* Header band, a step lighter than the card body so the two layers read
   distinctly (the card is SECONDARY_300).

   Flat rather than the previous gradient: the gradient faded toward the card
   colour on the right, which is exactly where the tag now sits — a filled pill
   needs an even field behind it, not one that dissolves into the body. Sits
   UNDER the watermark (z-index 0 vs 1) so the glyph passes across it, while the
   header's own text is lifted back above via titleGroupStyles.

   A low-alpha PRIMARY tint over a dark base, following the established
   alpha-suffix pattern used across SchedulerWizard and ScheduleStatusTag.
   Because it composites onto a dark colour it lands DARK and tinted rather than
   light — the band reads as event-branded instead of as another shade of grey,
   and it stays tenant-aware for free (amber on SportNinja, blue on Canlan)
   where a hand-picked hex would not.

   Two levers make it darker while staying in the PRIMARY family: a LOWER alpha
   (less accent composited in) and a DARKER base beneath it. Both are used here
   — 15% over SECONDARY_400, the game card's own colour.

   TESTING: `26` is 15%. Drop to `1A` (10%) for darker still, or raise to `33`
   (20%) to go back toward the previous, lighter band. */
const headerStyles = css`
  position: relative;
  z-index: 0;
  display: flex;
  align-items: center;
  /* Centres the title group on its own. The equal-flex side slots only render
     for the cancelled state now, and they still balance it there. */
  justify-content: center;
  gap: 16px;
  width: 100%;
  padding: 8px 16px;
  /* Base colour under the tint, so the alpha composites over a known dark value
     rather than whatever sits behind the card. */
  background-color: ${colors.SECONDARY_400};
  background-image: linear-gradient(${EVENT_ACCENT}26, ${EVENT_ACCENT}26);
  /* Tinted divider rather than plain SOFT_STEEL, so the band closes with the
     same accent it is tinted with instead of a grey seam. Slightly lighter
     (30%) than the card's outer border (40%) on purpose — this one separates
     two already-tinted layers, while the outer border has to hold its own
     against the page background. */
  border-bottom: 1px solid ${EVENT_ACCENT}4D;
`

/* Equal-flex side slots keep the centred title optically centred, whatever the
   Event label's width. */
const headerSideStyles = css`
  position: relative;
  z-index: 2;
  display: flex;
  flex: 1;
  min-width: 0;
`

const headerSideEndStyles = css`
  justify-content: flex-end;
`

/* Crest + name, filling the left slot that previously rendered as an empty
   spacer. Mirrors the game row, which also carries team identity at the outer
   edges — on a mixed list the eye finds "whose is this?" in the same place.

   `min-width: 0` on the row and an ellipsing name below it: the title keeps the
   centre, so a long team name shortens rather than pushing it off-centre. */
const teamStyles = css`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  /* "1 1 220px" rather than "0 0 220px": a fixed basis held its full width on
     a narrow card and starved the centre column, wrapping the date and address
     one word per line. Now it shrinks with the card, bounded by a max-width so
     it still cannot swallow the middle.

     Aligned left (not centred) so every row's crest starts at the same x — a
     ragged left column would make the centred dates wander down the list.

     This flex is mirrored EXACTLY by bodyDescriptionStyles opposite. Changing
     one without the other shifts the centre stack off the card's true middle,
     where it no longer lines up with the game row's date. */
  flex: 1 1 220px;
  max-width: 260px;
  min-width: 0;

  /* The reset matters more here than it looks: stacked, the 220px basis above
     would be read as a HEIGHT. Sized to its content instead. */
  @media (max-width: 768px) {
    flex: 0 0 auto;
    max-width: 100%;
    justify-content: center;
  }
`

/* 20px matches the header's cap height, so the crest sits on the same optical
   line as the title rather than inflating the band. `contain` never crops a
   badge-shaped logo, and the transparent-PNG crests carry their own padding. */
/* 60px — the exact size `TeamSection` gives the game card's crests, so a team
   reads at the same weight whichever row type it appears in. `contain` never
   crops a badge-shaped logo. */
const teamLogoStyles = css`
  width: 60px;
  height: 60px;
  object-fit: contain;
  flex-shrink: 0;
`

/* Deliberately quieter than the title: this identifies the row, it is not its
   subject. NEUTRAL_50 matches the body's secondary text. */
/* Only the truncation rules — size, family and weight all come from `Text`'s
   own heading5 handling, so this cannot drift from the game card. Muting for a
   cancelled row is passed as `color`, not set here. */
const teamNameStyle = `
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

/* Prefix + title travel together so the pair centres as one unit — centring
   them separately would leave the prefix drifting as the title's width
   changes. 6px rather than 8: the prefix already ends in a colon, so a wider
   gap reads as two separate labels. */
const titleGroupStyles = css`
  /* Above the watermark, which crosses the header background. */
  position: relative;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`

/* "Event:" — same size and weight as the title so the pair reads as one line,
   but PRIMARY-coloured so it registers as a type marker rather than the first
   word of the name. `flex-shrink: 0` keeps it intact when a long title
   ellipses. */
const typePrefixStyles = css`
  ${font.body}
  ${weightSemiBold}
  font-size: 16px;
  color: ${EVENT_ACCENT};
  flex-shrink: 0;
  white-space: nowrap;
`

const typePrefixMutedStyles = css`
  color: ${colors.NEUTRAL_50};
`

const titleStyles = css`
  /* Above the watermark, which crosses the header background. */
  position: relative;
  z-index: 2;
  ${font.body}
  ${weightSemiBold}
  font-size: 16px;
  color: ${colors.NEUTRAL_0};
  text-align: center;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

/* A cancelled event stays on the calendar marked off rather than disappearing —
   the API keeps it deliberately (a rained-out practice), and DELETE is what
   removes one. Struck-through title plus a red label, so the state is legible
   without the row losing its place in the list. */
const titleCancelledStyles = css`
  text-decoration: line-through;
  color: ${colors.NEUTRAL_50};
`

/* Category badge: a SOLID fill in the category's own colour, unlike the
   outlined CANCELLED pill. The fill is what carries the category — an outline
   at this size would leave too little colour to distinguish fourteen values.

   Text colour comes from `category.textColor` — computed by
   `getContrastTextColor` (the game scoreboard's helper) rather than assumed, so
   a future category colour cannot silently produce an unreadable badge.
   PRIMARY_BUTTON_CONTENT deliberately does not apply: that rule governs content
   on a `colors.PRIMARY` fill, and these are a fixed categorical palette rather
   than the tenant accent.

   Type matches the game row's badges (`badgeBaseStyles` in
   GameList/game-list-row/styles) so the two row types read as one column:
   12px / 14.4px line-height / 1.44px tracking, uppercase.

   `font.body`, NOT `font.title` — the game badge hardcodes `'Inter'`, which is
   the body face; `font.title` is Rift on SportNinja and rendered the event
   badges in a visibly different family. Going through the token rather than
   copying the hardcoded stack keeps Canlan on Gilroy. */
const categoryTagStyles = css`
  position: relative;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 3px 10px;
  border-radius: 4px;
  ${font.body}
  ${weightSemiBold}
  font-size: 12px;
  line-height: 15px;
  letter-spacing: 1.44px;
  text-transform: uppercase;
  white-space: nowrap;
`

/* Same pill geometry as the EVENT tag so the two states swap cleanly. Outlined
   rather than filled: a solid red block would out-shout every other row in the
   list, and cancelled is a modifier on the event, not a louder kind of it. */
const cancelledTagStyles = css`
  position: relative;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 3px 10px;
  border-radius: 4px;
  border: 1px solid ${colors.ERROR_100};
  ${font.body}
  ${weightSemiBold}
  font-size: 12px;
  line-height: 14.4px;
  letter-spacing: 1.44px;
  text-transform: uppercase;
  color: ${colors.ERROR_100};
`

/* The EVENT badge that used to sit at the header's right edge was removed —
   the calendar icon before the title carries the type now. Its outlined-pill
   styling lives on in `cancelledTagStyles` above, which was modelled on it, so
   restoring the badge means re-adding a `tagStyles` in that shape. */

/* A row rather than a column now: team on the left, the date/location stack
   centred, and a balancing spacer on the right. The stack keeps the card's
   optical centre, matching the game row's middle column. */
const bodyStyles = css`
  /* Above the watermark. */
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 16px;
  /* 8px 16px, matching the game row's contentRowStyles exactly — an event and
     a game sitting in the same list must have the same vertical rhythm, and
     the old 14px made the event row visibly taller than the game above it.

     No min-height either, for the same reason: the game row lets its content
     set the height, and pinning a floor here reintroduced the difference the
     padding change was meant to remove. */
  padding: 8px 16px;

  /* Below this the three columns cannot all hold their width — the team block
     stacks ABOVE the centred stack rather than squeezing it.

     A media query, NOT a container query: nothing in this tree declares
     container-type, so the container queries this file used to carry never
     matched at any width — which is what left the centre column wrapping one
     word per line on a narrow screen. 768px is the breakpoint the rest of the
     schedule list already uses. */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    /* The 69px floor was sized for a single row of content beside a 60px
       crest. Stacked, the rows supply their own height and the floor only adds
       slack. */
    min-height: 0;
  }
`

/* The original centred stack, unchanged — it just lives in the middle column
   now instead of being the whole body. */
/* Competition name over the category badge, as one block above the date.
   `gap: 4px` matches the centre column's own rhythm, so the three lines
   (competition, badge, date) step evenly rather than the first two bunching. */
const categoryGroupStyles = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-width: 0;
`

const bodyCenterStyles = css`
  display: flex;
  /* A real min-width, not "min-width: 0": the date and address are the card's
     payload, so the centre column gets a floor and the side columns give way,
     rather than the reverse. Below the floor the layout stacks anyway. */
  flex: 2 1 240px;
  min-width: 240px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  text-align: center;

  /* Stacked, the axis flips: a flex-basis is now a HEIGHT, so the 240px above
     would reserve 240px of vertical space (and a flex-grow of 2 would claim
     more still) — which made the card ~700px tall on a narrow screen.
     Reset to auto so it takes exactly the height of its own text. */
  @media (max-width: 768px) {
    flex: 0 0 auto;
    min-width: 0;
    width: 100%;
  }
`

/* The right column: the event's description, and the counterweight that keeps
   the centre stack in the true middle. Hidden once the layout stacks, where
   there is no left column to balance. */
const bodyDescriptionStyles = css`
  /* IDENTICAL flex to the team column opposite (see teamStyles). Not a
     preference — the centre stack is only centred against the CARD when both
     side columns resolve to the same width, and that is what lines an event
     row's date up with the game row's above it. Widening this side alone
     pushed the centre left by half the difference. */
  flex: 1 1 220px;
  max-width: 260px;
  min-width: 0;
  /* Sits toward the BOTTOM of the column rather than centred: the centre
     stack's own weight is low (the date and address sit under the badge), so a
     vertically-centred description read as floating above them. Bottom-aligning
     it, plus a little breathing room, lands it on the same optical line as the
     address — the row's other secondary detail. */
  display: flex;
  align-items: flex-end;
  /* Bottom-aligned, so a three-line description grows upward from the
     address's line instead of overflowing the card.

     The nudge is deliberately small: the row now takes the game card's 8px
     padding and has no min-height, so anything larger here would set the row's
     height itself and make an event taller than the game above it — undoing
     exactly what that padding change was for. */
  padding-top: 8px;

  /* Clamped to three lines: the row's height is set by the team column and the
     centre stack, and an unbounded description would grow the card past both.

     The clamp sits on the CHILD, not here — Text renders a <p>, and the clamp
     must apply to the element that actually holds the text. It also cannot go
     on this flex container, whose own display is already spoken for. */
  > p {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    overflow-wrap: anywhere;
    width: 100%;
  }

  /* Nothing to balance once the columns stack. */
  @media (max-width: 768px) {
    display: none;
  }
`

/* `flex-start`, not `center`: a two-line address centred the pin between both
   lines, leaving it floating in the middle of the block. It belongs on the
   FIRST line, the way it sits on a single-line address.

   The svg is then nudged down to centre it within that first line box rather
   than hanging from its top edge — the text is 12px at 145% (a ~17.4px line),
   the icon is 14px, so half the difference is ~2px. */
const locationRowStyles = css`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  overflow-wrap: anywhere;

  /* Targets the icon's WRAPPER span, which is the flex child — MapMarkerIcon
     renders <span class="anticon"><svg/></span>, so styling the svg alone
     would leave the span stretched and move nothing. */
  > .anticon {
    display: inline-flex;
    flex-shrink: 0;
    margin-top: 2px;
  }
`
